import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Switch,
  FormControlLabel,
  FormHelperText,
  Grid,
  Divider,
  Button,
  TextField,
  MenuItem,
  IconButton,
  InputAdornment,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import toast from 'react-hot-toast';
import {
  fetchFtpConfiguration,
  updateFtpConfiguration,
  fetchInputBuildConfiguration,
  updateInputBuildConfiguration,
  fetchIntakeSystemUsers,
} from '../../../services/api';
import useLoading from '../../../hooks/useLoading';
import IntakeCard, { emptyIntake, intakeFromApi, intakeToApi, validateIntake } from './IntakeCard';
import IntakeActivityDrawer from './IntakeActivityDrawer';

const PROTOCOLS = [
  { value: 'SFTP', label: 'SFTP (SSH)', defaultPort: 22 },
  { value: 'FTPS', label: 'FTPS (FTP over TLS)', defaultPort: 21 },
  { value: 'FTP', label: 'FTP (unencrypted)', defaultPort: 21 },
];

const AUTH_TYPES = [
  { value: 'PASSWORD', label: 'Password' },
  { value: 'SSH_KEY', label: 'SSH private key' },
];

const emptyConnection = {
  enabled: false,
  protocol: 'SFTP',
  host: '',
  port: 22,
  authType: 'PASSWORD',
  username: '',
  password: '',
  privateKey: '',
  passphrase: '',
  hostKeyFingerprint: '',
  passiveMode: true,
  implicitTls: false,
  remoteDirectory: '',
  // Set from the API: a secret is stored on the server. Secrets themselves are never sent back.
  hasPassword: false,
  hasPrivateKey: false,
  hasPassphrase: false,
};

// pollingIntervalMinutes, postDownloadAction and archiveDirectory are kept as stored but not shown: nothing acts on them.
// Files are only picked by hand on the File Upload page, which never moves or deletes them, and the SFTP intake has its
// own settings. LEAVE says what actually happens, and needs no archive directory.
const initialInbound = {
  ...emptyConnection,
  fileNamePattern: '*.csv',
  pollingIntervalMinutes: 60,
  postDownloadAction: 'LEAVE',
  archiveDirectory: '',
};

const initialOutbound = {
  ...emptyConnection,
  sameServerAsInbound: false,
  overwriteExisting: false,
  uploadAsTempThenRename: true,
};

// UI state is camelCase; the API (and the stored JSON) use snake_case.
const toSnake = (key) => key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
const toCamel = (key) => key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

const fromApi = (data, initial) => {
  const result = { ...initial };
  Object.entries(data || {}).forEach(([key, value]) => {
    const camel = toCamel(key);
    if (camel in initial && value !== null && value !== undefined) result[camel] = value;
  });
  return result;
};

const RESPONSE_ONLY = ['hasPassword', 'hasPrivateKey', 'hasPassphrase'];

const toApi = (config) => Object.fromEntries(
  Object.entries(config)
    .filter(([key]) => !RESPONSE_ONLY.includes(key))
    .map(([key, value]) => {
      const numeric = key === 'port' || key === 'pollingIntervalMinutes';
      return [toSnake(key), numeric ? (value === '' ? null : Number(value)) : value];
    })
);

const cardHeaderSx = {
  backgroundColor: '#f5f5f5',
  '& .MuiCardHeader-title': { fontSize: '1.1rem', fontWeight: 600 },
};

const sectionTitleSx = { fontWeight: 600, color: '#555', mb: 1.5 };

function validate(config, label, { skipConnection = false } = {}) {
  if (!config.enabled) return [];
  const errors = [];
  if (!skipConnection) {
    if (!config.host.trim()) errors.push(`${label}: Host is required`);
    const port = Number(config.port);
    if (!Number.isInteger(port) || port < 1 || port > 65535) errors.push(`${label}: Port must be between 1 and 65535`);
    if (!config.username.trim()) errors.push(`${label}: Username is required`);
    if (config.authType === 'SSH_KEY' && config.protocol === 'SFTP') {
      if (!config.privateKey.trim() && !config.hasPrivateKey) errors.push(`${label}: Private key is required`);
    } else if (!config.password && !config.hasPassword) {
      errors.push(`${label}: Password is required`);
    }
  }
  if (!config.remoteDirectory.trim()) errors.push(`${label}: Remote directory is required`);
  return errors;
}

function SecretField({ label, value, onChange, disabled, required, stored }) {
  const [show, setShow] = useState(false);
  return (
    <TextField
      label={label}
      type={show ? 'text' : 'password'}
      value={value}
      onChange={onChange}
      disabled={disabled}
      required={required && !stored}
      placeholder={stored ? '••••••••' : undefined}
      InputLabelProps={stored ? { shrink: true } : undefined}
      helperText={stored ? 'Saved. Leave blank to keep it.' : undefined}
      fullWidth
      size="small"
      autoComplete="new-password"
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton size="small" onClick={() => setShow((s) => !s)} edge="end" disabled={disabled}>
              {show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}

function ConnectionFields({ config, onChange, disabled }) {
  const isSftp = config.protocol === 'SFTP';
  const useKey = isSftp && config.authType === 'SSH_KEY';

  const handleProtocolChange = (protocol) => {
    const { defaultPort } = PROTOCOLS.find((p) => p.value === protocol);
    const port = config.implicitTls && protocol === 'FTPS' ? 990 : defaultPort;
    onChange({ protocol, port, authType: protocol === 'SFTP' ? config.authType : 'PASSWORD' });
  };

  return (
    <>
      <Typography variant="subtitle2" sx={sectionTitleSx}>Server</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <TextField
            select
            label="Protocol"
            value={config.protocol}
            onChange={(e) => handleProtocolChange(e.target.value)}
            disabled={disabled}
            fullWidth
            size="small"
          >
            {PROTOCOLS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Host"
            placeholder="sftp.example.com"
            value={config.host}
            onChange={(e) => onChange({ host: e.target.value })}
            disabled={disabled}
            required
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Port"
            type="number"
            value={config.port}
            onChange={(e) => onChange({ port: e.target.value })}
            disabled={disabled}
            required
            fullWidth
            size="small"
            inputProps={{ min: 1, max: 65535 }}
          />
        </Grid>
        {isSftp && (
          <Grid item xs={12}>
            <TextField
              label="Host key fingerprint"
              placeholder="SHA256:..."
              helperText="Optional. When set, the connection is refused if the server's host key does not match."
              value={config.hostKeyFingerprint}
              onChange={(e) => onChange({ hostKeyFingerprint: e.target.value })}
              disabled={disabled}
              fullWidth
              size="small"
            />
          </Grid>
        )}
        {!isSftp && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.passiveMode}
                    onChange={(e) => onChange({ passiveMode: e.target.checked })}
                    disabled={disabled}
                  />
                }
                label="Passive mode"
              />
              {config.protocol === 'FTPS' && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.implicitTls}
                      onChange={(e) => onChange({ implicitTls: e.target.checked, port: e.target.checked ? 990 : 21 })}
                      disabled={disabled}
                    />
                  }
                  label="Implicit TLS"
                />
              )}
            </Box>
          </Grid>
        )}
      </Grid>

      <Typography variant="subtitle2" sx={sectionTitleSx}>Authentication</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {isSftp && (
          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Authentication type"
              value={config.authType}
              onChange={(e) => onChange({ authType: e.target.value })}
              disabled={disabled}
              fullWidth
              size="small"
            >
              {AUTH_TYPES.map((a) => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
            </TextField>
          </Grid>
        )}
        <Grid item xs={12} md={isSftp ? 4 : 6}>
          <TextField
            label="Username"
            value={config.username}
            onChange={(e) => onChange({ username: e.target.value })}
            disabled={disabled}
            required
            fullWidth
            size="small"
            autoComplete="off"
          />
        </Grid>
        {!useKey && (
          <Grid item xs={12} md={isSftp ? 4 : 6}>
            <SecretField
              label="Password"
              value={config.password}
              onChange={(e) => onChange({ password: e.target.value })}
              disabled={disabled}
              required
              stored={config.hasPassword}
            />
          </Grid>
        )}
        {useKey && (
          <>
            <Grid item xs={12} md={4}>
              <SecretField
                label="Key passphrase"
                value={config.passphrase}
                onChange={(e) => onChange({ passphrase: e.target.value })}
                disabled={disabled}
                stored={config.hasPassphrase}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Private key"
                placeholder={config.hasPrivateKey ? 'A key is saved. Paste a new one to replace it.' : '-----BEGIN OPENSSH PRIVATE KEY-----'}
                value={config.privateKey}
                onChange={(e) => onChange({ privateKey: e.target.value })}
                disabled={disabled}
                required={!config.hasPrivateKey}
                InputLabelProps={config.hasPrivateKey ? { shrink: true } : undefined}
                multiline
                minRows={4}
                fullWidth
                size="small"
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
              />
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
}

function InboundCard({ config, onChange }) {
  const disabled = !config.enabled;
  return (
    <Card elevation={3} sx={{ width: '100%' }}>
      <CardHeader
        title="Inbound"
        subheader="Pick up provider files from the partner's server"
        action={
          <FormControlLabel
            control={<Switch checked={config.enabled} onChange={(e) => onChange({ enabled: e.target.checked })} />}
            label="Enabled"
          />
        }
        sx={cardHeaderSx}
      />
      <CardContent sx={{ p: 3 }}>
        <ConnectionFields config={config} onChange={onChange} disabled={disabled} />

        <Typography variant="subtitle2" sx={sectionTitleSx}>Files</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Remote directory"
              placeholder="/outgoing/rosters"
              value={config.remoteDirectory}
              onChange={(e) => onChange({ remoteDirectory: e.target.value })}
              disabled={disabled}
              required
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="File name pattern"
              placeholder="*.csv"
              helperText="CSV files offered when picking from FTP on the File Upload page. Wildcards * and ? are supported."
              value={config.fileNamePattern}
              onChange={(e) => onChange({ fileNamePattern: e.target.value })}
              disabled={disabled}
              fullWidth
              size="small"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

function OutboundCard({ config, onChange, inboundEnabled }) {
  const disabled = !config.enabled;
  const reuseInbound = config.sameServerAsInbound && inboundEnabled;
  return (
    <Card elevation={3} sx={{ width: '100%' }}>
      <CardHeader
        title="Outbound"
        subheader="Deliver generated output files to the partner's server"
        action={
          <FormControlLabel
            control={<Switch checked={config.enabled} onChange={(e) => onChange({ enabled: e.target.checked })} />}
            label="Enabled"
          />
        }
        sx={cardHeaderSx}
      />
      <CardContent sx={{ p: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={config.sameServerAsInbound}
              onChange={(e) => onChange({ sameServerAsInbound: e.target.checked })}
              disabled={disabled || !inboundEnabled}
            />
          }
          label="Use the same server and credentials as inbound"
          sx={{ mb: 2 }}
        />
        <Divider sx={{ mb: 2 }} />

        {!reuseInbound && <ConnectionFields config={config} onChange={onChange} disabled={disabled} />}

        <Typography variant="subtitle2" sx={sectionTitleSx}>Files</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Remote directory"
              placeholder="/incoming/output"
              value={config.remoteDirectory}
              onChange={(e) => onChange({ remoteDirectory: e.target.value })}
              disabled={disabled}
              required
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.overwriteExisting}
                    onChange={(e) => onChange({ overwriteExisting: e.target.checked })}
                    disabled={disabled}
                  />
                }
                label="Overwrite a file with the same name"
              />
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.uploadAsTempThenRename}
                      onChange={(e) => onChange({ uploadAsTempThenRename: e.target.checked })}
                      disabled={disabled}
                    />
                  }
                  label="Upload under a temporary name, then rename"
                />
                {/* Indented to line up with the label text, past the switch. */}
                <FormHelperText disabled={disabled} sx={{ mt: -0.5, ml: '47px' }}>
                  Prevents the partner from picking up a partly uploaded file
                </FormHelperText>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

// What the intake card saves, to tell whether it changed: an account that never touches it gets no input build row.
const intakeSnapshot = (stage, intake) => JSON.stringify({ stage, intake: intakeToApi(intake) });

export default function FtpConfig() {
  const [inbound, setInbound] = useState(initialInbound);
  const [outbound, setOutbound] = useState(initialOutbound);
  // 'loading' | 'ready' | 'error'. Save is only possible once the stored config is in the form:
  // saving the empty initial form would overwrite the stored configuration.
  const [loadState, setLoadState] = useState('loading');
  // The account's input build configuration, whose "intake" and "stage" the SFTP intake card edits; the rest of it
  // (the older "roster_csv", now set up on Roster Return File) is saved back as it was. Loaded beside the FTP
  // configuration but separately: if it can't be read, the card says so and the rest of the screen still works.
  const [inputBuild, setInputBuild] = useState(null);
  const [intake, setIntake] = useState(emptyIntake);
  const [stage, setStage] = useState('shadow');
  const [savedIntake, setSavedIntake] = useState('');
  const [intakeState, setIntakeState] = useState('loading');
  const [systemUsers, setSystemUsers] = useState([]);
  const [activityOpen, setActivityOpen] = useState(false);
  const { withLoading } = useLoading();

  const updateInbound = (patch) => setInbound((prev) => ({ ...prev, ...patch }));
  const updateOutbound = (patch) => setOutbound((prev) => ({ ...prev, ...patch }));
  const updateIntake = (patch) => setIntake((prev) => ({ ...prev, ...patch }));

  const applyResponse = (data) => {
    setInbound(fromApi(data?.inbound, initialInbound));
    setOutbound(fromApi(data?.outbound, initialOutbound));
  };

  const applyInputBuild = (config) => {
    const form = intakeFromApi(config?.intake);
    const formStage = config?.stage === 'live' ? 'live' : 'shadow';
    setInputBuild(config);
    setIntake(form);
    setStage(formStage);
    setSavedIntake(intakeSnapshot(formStage, form));
  };

  const load = useCallback(async () => {
    setLoadState('loading');
    setIntakeState('loading');
    await withLoading(async () => {
      const [ftp, inputBuildRes, usersRes] = await Promise.allSettled([
        fetchFtpConfiguration(),
        fetchInputBuildConfiguration(),
        fetchIntakeSystemUsers(),
      ]);
      if (ftp.status === 'fulfilled') {
        setInbound(fromApi(ftp.value?.response_data?.inbound, initialInbound));
        setOutbound(fromApi(ftp.value?.response_data?.outbound, initialOutbound));
        setLoadState('ready');
      } else {
        console.error('Error fetching FTP configuration:', ftp.reason);
        setLoadState('error');
      }
      if (inputBuildRes.status === 'fulfilled' && inputBuildRes.value?.response_data) {
        applyInputBuild(inputBuildRes.value.response_data);
        setIntakeState('ready');
      } else {
        console.error('Error fetching the SFTP intake settings:', inputBuildRes.reason);
        setIntakeState('error');
      }
      setSystemUsers(usersRes.status === 'fulfilled' ? usersRes.value?.response_data || [] : []);
    });
  }, [withLoading]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    if (loadState !== 'ready') return;
    const reuseInbound = outbound.sameServerAsInbound && inbound.enabled;
    const intakeChanged = intakeState === 'ready' && intakeSnapshot(stage, intake) !== savedIntake;
    const errors = [
      ...validate(inbound, 'Inbound'),
      ...validate(outbound, 'Outbound', { skipConnection: reuseInbound }),
      // An intake that is on is checked even when unchanged: switching Inbound off here would cut it off.
      ...(intakeState === 'ready' && (intakeChanged || intake.enabled) ? validateIntake(intake, inbound.enabled) : []),
    ];
    if (errors.length) {
      toast.error(errors.join('\n'));
      return;
    }

    await withLoading(async () => {
      try {
        const res = await updateFtpConfiguration({
          inbound: toApi(inbound),
          outbound: toApi({ ...outbound, sameServerAsInbound: reuseInbound }),
        });
        applyResponse(res?.response_data);
      } catch (error) {
        // 4xx/5xx messages are already shown by the API client interceptor.
        console.error('Error saving FTP configuration:', error);
        return;
      }

      if (intakeChanged) {
        try {
          const res = await updateInputBuildConfiguration({
            ...inputBuild, stage, intake: intakeToApi(intake),
          });
          applyInputBuild(res?.response_data);
        } catch (error) {
          // The FTP settings are saved; the intake's are not, and the cards keep what was entered.
          console.error('Error saving the SFTP intake settings:', error);
          toast.error('FTP configuration saved, but the SFTP intake settings were not. Check them and save again.');
          return;
        }
      }
      toast.success(intakeChanged ? 'FTP configuration and SFTP intake saved' : 'FTP configuration saved');
    });
  };

  return (
    <Box p={3} sx={{ height: '100%', overflowY: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
          FTP Configuration
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={load} disabled={loadState === 'loading'}>Discard changes</Button>
          <Button variant="contained" onClick={handleSave} disabled={loadState !== 'ready'}>Save</Button>
        </Box>
      </Box>

      {loadState === 'error' && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
        >
          The saved FTP configuration could not be loaded, so it cannot be changed right now.
        </Alert>
      )}

      {loadState === 'ready' && (
      <Grid container spacing={3} direction="column">
        <Grid item xs={12}>
          <InboundCard config={inbound} onChange={updateInbound} />
        </Grid>
        <Grid item xs={12}>
          <OutboundCard config={outbound} onChange={updateOutbound} inboundEnabled={inbound.enabled} />
        </Grid>
        <Grid item xs={12}>
          {intakeState === 'error' ? (
            <Alert
              severity="warning"
              action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
            >
              The SFTP intake settings could not be loaded, so they cannot be changed right now.
            </Alert>
          ) : intakeState === 'ready' && (
            <IntakeCard
              intake={intake}
              stage={stage}
              onIntakeChange={updateIntake}
              onStageChange={setStage}
              inboundEnabled={inbound.enabled}
              inboundDirectory={inbound.remoteDirectory}
              systemUsers={systemUsers}
              onViewActivity={() => setActivityOpen(true)}
            />
          )}
        </Grid>
      </Grid>
      )}
      <IntakeActivityDrawer open={activityOpen} onClose={() => setActivityOpen(false)} />
    </Box>
  );
}
